import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { convertImageToWebp, supportsWebpEncoding } from '../utils/convertImageToWebp'
import { uploadImageToS3 } from '../utils/uploadImageToS3'

export interface ImageUploadOptions {
  maxSizeBytes?: number
  maxWidth?: number
  maxHeight?: number
  quality?: number
}

export interface ImageUploadReturn {
  upload: (file: File) => Promise<string>
  isUploading: boolean
  error: Error | null
  reset: () => void
}

type ImageMimeType = 'image/jpeg' | 'image/png' | 'image/webp'

type GetUploadUrlFn = (options: {
  query: { fileName: string; mimeType: ImageMimeType; fileSizeBytes: number }
}) => Promise<{
  data?: { uploadUrl: string; imageUrl: string }
  error?: unknown
}>

const DEFAULT_MAX_SIZE_BYTES = 10 * 1024 * 1024 // 10MB

export function createImageUploadHook(getUploadUrl: GetUploadUrlFn) {
  return function useImageUpload(options?: ImageUploadOptions): ImageUploadReturn {
    const { t } = useTranslation()
    const [isUploading, setIsUploading] = useState(false)
    const [error, setError] = useState<Error | null>(null)
    const maxSizeBytes = options?.maxSizeBytes ?? DEFAULT_MAX_SIZE_BYTES

    const upload = useCallback(
      async (file: File): Promise<string> => {
        setIsUploading(true)
        setError(null)

        const webpSupported = supportsWebpEncoding()

        try {
          if (file.size > maxSizeBytes) {
            throw new Error(
              t('common.errors.fileSizeExceeded', { max: maxSizeBytes / 1024 / 1024 }),
            )
          }

          const blob = await convertImageToWebp(file, {
            maxWidth: options?.maxWidth,
            maxHeight: options?.maxHeight,
            quality: options?.quality,
          })

          const mimeType: ImageMimeType = webpSupported ? 'image/webp' : 'image/jpeg'
          const extension = webpSupported ? '.webp' : '.jpg'
          const fileName = file.name.replace(/\.[^.]+$/, extension)

          const response = await getUploadUrl({
            query: { fileName, mimeType, fileSizeBytes: blob.size },
          })
          if (!response.data) {
            throw new Error(t('common.errors.failedToGetUploadUrl'))
          }

          await uploadImageToS3(blob, response.data.uploadUrl)

          return response.data.imageUrl
        } catch (error_) {
          const uploadError =
            error_ instanceof Error ? error_ : new Error(t('common.errors.uploadFailed'))
          setError(uploadError)
          throw uploadError
        } finally {
          setIsUploading(false)
        }
      },
      [maxSizeBytes, options?.maxWidth, options?.maxHeight, options?.quality, t],
    )

    const reset = useCallback(() => {
      setError(null)
      setIsUploading(false)
    }, [])

    return { upload, isUploading, error, reset }
  }
}
